import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  ShieldCheck, Clock, Send, Loader2, Upload, X, Check,
  Lock, EyeOff, UserX, ScrollText, ChevronRight, AlertOctagon,
} from 'lucide-react';
import TopNav from '../components/sections/TopNav';
import SiteFooter from '../components/sections/SiteFooter';
import TurnstileWidget from '../components/TurnstileWidget';
import { useSeoMeta, useNoIndex } from '../utils/jsonLd';

const API = process.env.REACT_APP_BACKEND_URL;
const WINDOW_HOURS = 72;

// ------------------------- helpers -------------------------

async function uploadFileToR2(file) {
  const presign = await fetch(`${API}/api/experiences/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename: file.name, content_type: file.type }),
  });
  if (!presign.ok) {
    const err = await presign.json().catch(() => ({}));
    throw new Error(err.detail || 'Could not prepare upload');
  }
  const { upload_url, public_url } = await presign.json();
  const put = await fetch(upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!put.ok) throw new Error('Upload failed');
  return public_url;
}

function formatDateline(iso) {
  if (!iso) return 'Date withheld';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('en-AU', {
    day: 'numeric', month: 'long', year: 'numeric',
  }).toUpperCase();
}

// Compute {days, hours, minutes, seconds} left in the window from an ISO
// `opened_at`. Returns null if the window isn't open or is already over.
function useCountdown(openedAtIso, active) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return useMemo(() => {
    if (!active || !openedAtIso) return null;
    const opened = new Date(openedAtIso).getTime();
    if (isNaN(opened)) return null;
    const endsAt = opened + WINDOW_HOURS * 3600 * 1000;
    const remaining = Math.max(0, endsAt - now);
    if (remaining === 0) return { hh: '00', mm: '00', ss: '00', pctSpent: 100 };
    const hh = Math.floor(remaining / 3_600_000);
    const mm = Math.floor((remaining % 3_600_000) / 60_000);
    const ss = Math.floor((remaining % 60_000) / 1000);
    const total = WINDOW_HOURS * 3600 * 1000;
    const pctSpent = Math.min(100, Math.max(0, 100 - (remaining / total) * 100));
    return {
      hh: String(hh).padStart(2, '0'),
      mm: String(mm).padStart(2, '0'),
      ss: String(ss).padStart(2, '0'),
      pctSpent,
    };
  }, [now, openedAtIso, active]);
}

// ------------------------- component -------------------------

export default function Experiences() {
  useSeoMeta({
    title: 'The Madame Files - Anonymous Reports · Outlaw Media',
    description: 'A confidential 72-hour reporting window for women who have been abused by industry figures posing as "Madames". All submissions are anonymised.',
  });
  useNoIndex(true);

  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // form state
  const [whenIso, setWhenIso] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [instagram, setInstagram] = useState('');
  const [files, setFiles] = useState([]);
  const [publishMedia, setPublishMedia] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [website, setWebsite] = useState(''); // honeypot - real users leave blank

  const load = useCallback(async () => {
    try {
      const [sRes, iRes] = await Promise.all([
        fetch(`${API}/api/experiences/status`),
        fetch(`${API}/api/experiences`),
      ]);
      if (sRes.ok) setStatus(await sRes.json());
      if (iRes.ok) {
        const d = await iRes.json();
        setItems(d.items || []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const countdown = useCountdown(status?.opened_at, status?.active);

  const handleFileChange = (e) => {
    const list = Array.from(e.target.files || []).slice(0, 8);
    setFiles(list);
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (description.trim().length < 10) {
      toast.error('Please describe what happened (minimum 10 characters).');
      return;
    }
    if (!location.trim()) {
      toast.error('Please provide a location.');
      return;
    }
    setSubmitting(true);
    try {
      let media_urls = [];
      if (files.length > 0) {
        setUploading(true);
        for (const f of files) {
          const url = await uploadFileToR2(f);
          media_urls.push(url);
        }
        setUploading(false);
      }
      const res = await fetch(`${API}/api/experiences/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          when_iso: whenIso || null,
          location: location.trim(),
          description: description.trim(),
          media_urls,
          instagram_handle: instagram.trim() || null,
          publish_media: files.length > 0 && publishMedia,
          turnstile_token: turnstileToken,
          website,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Submission failed');
      }
      const data = await res.json();
      setSubmitResult(data);
      setWhenIso(''); setLocation(''); setDescription(''); setInstagram('');
      setFiles([]);
      setPublishMedia(false);
      toast.success('Report submitted - thank you');
      // Scroll back to the top so the confirmation banner (which sits above
      // the form) is the first thing the reader sees.
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    } catch (err) {
      toast.error(err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  const windowClosed = !loading && status && !status.active;
  const confirmedCount = items.filter((i) => i.is_confirmed).length;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="experiences-page">
      <TopNav />

      {/* ============================================================== */}
      {/*                            HERO                                 */}
      {/* ============================================================== */}
      <header className="relative border-b border-zinc-900 overflow-hidden">
        {/* Layered dark gradient panels - no neon, just editorial depth */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-[#111113] via-[#0a0a0a] to-[#0a0a0a]" />
          <div
            className="absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                'repeating-linear-gradient(90deg, rgba(255,255,255,0.05) 0 1px, transparent 1px 80px)',
            }}
          />
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-red-600" />
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-flex items-center gap-2 bg-red-600 text-white text-[10px] font-black uppercase tracking-[0.35em] px-3 py-1.5">
              <AlertOctagon className="h-3 w-3" /> Special Investigation
            </span>
            <span className="text-zinc-500 text-[11px] font-mono uppercase tracking-widest">
              Vol. 01 · Confidential
            </span>
          </div>

          <h1 className="text-white font-black leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl mb-4">
            The Madame<br />
            <span className="text-red-500">Files</span>
          </h1>

          <p className="text-zinc-300 text-lg sm:text-xl max-w-2xl leading-snug font-serif italic mb-6">
            A confidential 72-hour reporting window for women who have been
            abused by figures positioning themselves as &ldquo;Madames&rdquo;
            inside the industry - starting with the woman known as{' '}
            <span className="text-white not-italic font-bold">Jasmine</span>{' '}
            (Cindy Leef).
          </p>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-widest font-bold text-zinc-500">
            <span>Reported by <span className="text-white">Ryan Naumenko</span></span>
            <span className="text-zinc-700">·</span>
            <span>Outlaw Media Investigations Desk</span>
            <span className="text-zinc-700">·</span>
            <span className="text-red-400">Anonymous by design</span>
          </div>
        </div>
      </header>

      {/* ============================================================== */}
      {/*                    LIVE COUNTDOWN / METRIC BAR                  */}
      {/* ============================================================== */}
      <section className="border-b border-zinc-900 bg-[#0d0d0f]" data-testid="experiences-window-status">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="flex items-baseline gap-4">
              <Clock className={`h-4 w-4 mb-0.5 ${status?.active ? 'text-red-500' : 'text-zinc-600'}`} />
              <div>
                <p className="text-zinc-500 text-[10px] uppercase tracking-[0.3em] font-bold mb-1">
                  {status?.active ? 'Window closes in' : 'Reporting window'}
                </p>
                {status?.active && countdown ? (
                  <p className="text-white font-black text-3xl sm:text-4xl font-mono tabular-nums leading-none">
                    {countdown.hh}<span className="text-red-500 mx-1">:</span>
                    {countdown.mm}<span className="text-red-500 mx-1">:</span>
                    {countdown.ss}
                  </p>
                ) : (
                  <p className="text-zinc-400 font-bold text-lg leading-none">Currently closed</p>
                )}
              </div>
            </div>

            <div className="flex items-baseline gap-8">
              <div>
                <p className="text-zinc-500 text-[10px] uppercase tracking-[0.3em] font-bold mb-1">
                  Published
                </p>
                <p className="text-white font-black text-3xl sm:text-4xl font-mono tabular-nums leading-none">
                  {String(items.length).padStart(2, '0')}
                </p>
              </div>
              <div>
                <p className="text-zinc-500 text-[10px] uppercase tracking-[0.3em] font-bold mb-1">
                  ✓ Confirmed
                </p>
                <p className="text-emerald-400 font-black text-3xl sm:text-4xl font-mono tabular-nums leading-none">
                  {String(confirmedCount).padStart(2, '0')}
                </p>
              </div>
            </div>
          </div>
          {status?.active && countdown && (
            <div className="mt-4 h-[3px] bg-zinc-900 overflow-hidden">
              <div
                className="h-full bg-red-600 transition-all duration-1000"
                style={{ width: `${countdown.pctSpent}%` }}
              />
            </div>
          )}
        </div>
      </section>

      {/* ============================================================== */}
      {/*                       EDITOR'S LETTER                           */}
      {/* ============================================================== */}
      <section className="border-b border-zinc-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <p className="text-red-500 text-[10px] font-black uppercase tracking-[0.35em] mb-4">
            From the editor
          </p>
          <div className="prose prose-invert max-w-none font-serif text-zinc-200 text-lg leading-[1.7]">
            <p className="first-letter:text-6xl first-letter:font-black first-letter:mr-3 first-letter:float-left first-letter:leading-none first-letter:text-red-500 first-letter:font-sans">
              Since I started reporting on this Swans saga, a name has
              circulated in the whispers women exchange when they warn each
              other about who to avoid. Jasmine, aka Cindy Leef. A woman who
              describes herself as &ldquo;a Madame&rdquo; and who multiple sources
              allege has coerced, exploited, or physically hurt women working
              alongside or for her.
            </p>
            <p>
              We&apos;re not going to publish those allegations without evidence.
              But we are going to open a window - 72 hours - where any woman
              with a story to tell can put it on the record, on her own terms,
              in her own words, without ever giving us her name.
            </p>
            <p>
              If you&apos;ve been hurt and you can help another woman avoid the
              same thing, this page is for you. If you&apos;re not ready to talk to
              police, we get it. What you tell us stays on this page - under a
              number, not a name - and only if you say so.
            </p>
            <p className="text-zinc-400 text-sm not-italic font-sans mt-8 pt-4 border-t border-zinc-900">
              - Ryan Naumenko, Editor · Outlaw Media
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/*                     THE PROMISES / TRUST STRIP                  */}
      {/* ============================================================== */}
      <section className="border-b border-zinc-900 bg-[#0d0d0f]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <p className="text-zinc-500 text-[10px] font-black uppercase tracking-[0.35em] mb-6 text-center">
            What we promise you
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { Icon: UserX, title: 'Anonymous', body: 'No name, no email, no phone. Ever.' },
              { Icon: Lock, title: 'Encrypted', body: 'All submissions travel over HTTPS.' },
              { Icon: EyeOff, title: 'Never identified', body: 'You appear as "Experience N" only.' },
              { Icon: ScrollText, title: 'Legally reviewed', body: 'Nothing publishes until it is safe to.' },
            ].map(({ Icon, title, body }) => (
              <div key={title} className="text-center">
                <div className="inline-flex items-center justify-center w-11 h-11 border border-red-900/60 bg-red-950/20 rounded-full mb-3">
                  <Icon className="h-5 w-5 text-red-400" />
                </div>
                <p className="text-white font-black text-[11px] uppercase tracking-widest mb-1">{title}</p>
                <p className="text-zinc-500 text-xs leading-snug">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/*                       POST-SUBMIT BANNER                        */}
      {/* ============================================================== */}
      {submitResult && (
        <section id="submit-result" className="border-b border-zinc-900 bg-emerald-950/20">
          <div
            className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
            data-testid="experiences-submit-result"
          >
            <p className="text-emerald-300 font-black text-lg mb-2 flex items-center gap-2">
              <Check className="h-5 w-5" /> Thank you for your submission
            </p>
            <p className="text-emerald-100 text-sm">
              We will review your experience report soon and if approved,
              it will be published to this page.
            </p>
          </div>
        </section>
      )}

      {/* ============================================================== */}
      {/*                          THE FORM                               */}
      {/* ============================================================== */}
      {status?.active && (
        <section className="border-b border-zinc-900">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <div className="flex items-baseline justify-between mb-8">
              <div>
                <p className="text-red-500 text-[10px] font-black uppercase tracking-[0.35em] mb-2">
                  File a report
                </p>
                <h2 className="text-white font-black text-3xl sm:text-4xl tracking-tight">
                  Tell it on the record.
                </h2>
              </div>
              <div className="hidden sm:block text-zinc-600 text-[10px] font-mono uppercase tracking-widest text-right">
                Est. 5 min<br />4 fields
              </div>
            </div>

            <form
              onSubmit={submit}
              className="space-y-8"
              data-testid="experiences-form"
            >
              {/* 01 - WHEN */}
              <div className="grid grid-cols-[36px_1fr] gap-x-4 items-start">
                <div className="text-red-500 font-mono font-black text-lg leading-none pt-1">01</div>
                <div>
                  <label htmlFor="exp-when" className="block text-white text-sm font-bold mb-2">
                    When did it happen?
                    <span className="text-zinc-500 font-normal text-xs ml-2">Optional</span>
                  </label>
                  <input
                    id="exp-when"
                    type="date"
                    value={whenIso}
                    onChange={(e) => setWhenIso(e.target.value)}
                    className="w-full bg-transparent border-0 border-b border-zinc-700 focus:border-red-500 focus:outline-none py-2 text-white text-base font-mono"
                    data-testid="experiences-when-input"
                  />
                  <p className="text-zinc-600 text-[11px] mt-1">Leave blank if you don&apos;t remember exactly.</p>
                </div>
              </div>

              {/* 02 - WHERE */}
              <div className="grid grid-cols-[36px_1fr] gap-x-4 items-start">
                <div className="text-red-500 font-mono font-black text-lg leading-none pt-1">02</div>
                <div>
                  <label htmlFor="exp-where" className="block text-white text-sm font-bold mb-2">
                    Where did it happen? *
                  </label>
                  <input
                    id="exp-where"
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Melbourne · hotel · private residence …"
                    maxLength={200}
                    required
                    className="w-full bg-transparent border-0 border-b border-zinc-700 focus:border-red-500 focus:outline-none py-2 text-white text-base placeholder:text-zinc-700"
                    data-testid="experiences-where-input"
                  />
                </div>
              </div>

              {/* 03 - WHAT */}
              <div className="grid grid-cols-[36px_1fr] gap-x-4 items-start">
                <div className="text-red-500 font-mono font-black text-lg leading-none pt-1">03</div>
                <div>
                  <label htmlFor="exp-what" className="block text-white text-sm font-bold mb-2">
                    What happened? *
                  </label>
                  <textarea
                    id="exp-what"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Tell us what happened, in as much detail as you're comfortable with. We'll never publish anything that could identify you."
                    rows={8}
                    maxLength={6000}
                    required
                    className="w-full bg-transparent border border-zinc-800 focus:border-red-500 focus:outline-none py-3 px-3 text-white text-base leading-relaxed placeholder:text-zinc-700 resize-y"
                    data-testid="experiences-what-input"
                  />
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-zinc-600 text-[11px]">Minimum 10 characters.</p>
                    <p className={`text-[11px] font-mono ${description.length > 5500 ? 'text-red-400' : 'text-zinc-600'}`}>
                      {description.length} / 6000
                    </p>
                  </div>

                  {/* Files sub-section */}
                  <div className="mt-4">
                    <label htmlFor="exp-files" className="text-zinc-400 text-xs font-bold uppercase tracking-widest mb-2 block">
                      Photos or videos <span className="normal-case text-zinc-600 font-normal tracking-normal">(optional · up to 8)</span>
                    </label>
                    <label
                      htmlFor="exp-files"
                      className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 border-dashed hover:border-red-500/60 rounded px-3 py-3 cursor-pointer transition-colors"
                    >
                      <Upload className="h-4 w-4 text-zinc-500" />
                      <span className="text-zinc-500 text-sm">
                        {files.length > 0 ? `${files.length} file${files.length === 1 ? '' : 's'} attached` : 'Add evidence…'}
                      </span>
                    </label>
                    <input
                      id="exp-files" type="file" onChange={handleFileChange}
                      multiple accept="image/*,video/*" className="hidden"
                      data-testid="experiences-files-input"
                    />
                    {files.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {files.map((f, idx) => (
                          <li key={idx} className="flex items-center justify-between text-xs text-zinc-400 bg-zinc-950 rounded px-2 py-1.5">
                            <span className="truncate mr-2 font-mono">{f.name}</span>
                            <button type="button" onClick={() => removeFile(idx)} className="text-zinc-500 hover:text-red-400">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Publish consent - only appears when files have been
                        attached. Default OFF so submitters have to explicitly
                        opt in to publishing their own media. */}
                    {files.length > 0 && (
                      <label className="mt-3 flex items-start gap-2.5 bg-red-950/20 border border-red-900/40 rounded p-3 cursor-pointer hover:bg-red-950/30 transition-colors">
                        <input
                          type="checkbox"
                          checked={publishMedia}
                          onChange={(e) => setPublishMedia(e.target.checked)}
                          className="mt-0.5 accent-red-500 h-4 w-4 cursor-pointer"
                          data-testid="experiences-publish-media-checkbox"
                        />
                        <span className="text-red-100 text-xs leading-relaxed">
                          <span className="font-bold text-white">Publish these photos/videos with my report.</span><br />
                          Leave unticked to keep this evidence private -
                          our editor will still see it for verification,
                          but nothing visual will appear on the public page.
                        </span>
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* 04 - VERIFICATION */}
              <div className="grid grid-cols-[36px_1fr] gap-x-4 items-start">
                <div className="text-red-500 font-mono font-black text-lg leading-none pt-1">04</div>
                <div>
                  <label htmlFor="exp-ig" className="block text-white text-sm font-bold mb-2">
                    Instagram <span className="text-emerald-400 font-normal text-xs ml-1">Eligible for ✓ CONFIRMED mark</span>
                  </label>
                  <div className="flex items-stretch max-w-md">
                    <span className="bg-transparent border-0 border-b border-zinc-700 px-2 flex items-center text-zinc-500 text-base font-mono">@</span>
                    <input
                      id="exp-ig"
                      type="text"
                      value={instagram}
                      onChange={(e) => setInstagram(e.target.value)}
                      placeholder="yourhandle"
                      maxLength={40}
                      className="flex-1 bg-transparent border-0 border-b border-zinc-700 focus:border-emerald-500 focus:outline-none py-2 text-white text-base font-mono placeholder:text-zinc-700"
                      data-testid="experiences-ig-input"
                    />
                  </div>
                  <p className="text-zinc-600 text-[11px] mt-2 max-w-md leading-relaxed">
                    Used only for our internal verification. <span className="text-zinc-400 font-semibold">Never displayed publicly.</span>{' '}
                    Reports with a verified handle earn a ✓ CONFIRMED mark once approved.
                  </p>
                </div>
              </div>

              {/* Submit - spans the full form width, sealed off with a red
                  rule so it reads as the terminal action of the report. */}
              <div className="pt-6 border-t border-zinc-900 mt-8">
                {/* Honeypot - hidden from real users. Bots fill it. */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  aria-hidden="true"
                  style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
                />
                {/* Cloudflare Turnstile - invisible when the visitor looks
                    trusted, interactive checkbox otherwise. Fails closed. */}
                <div className="flex justify-center mb-4">
                  <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken(null)} />
                </div>
                <button
                  type="submit"
                  disabled={submitting || uploading}
                  className="group relative w-full bg-red-600 hover:bg-red-500 disabled:bg-zinc-900 disabled:text-zinc-600 disabled:cursor-not-allowed text-white font-black py-4 sm:py-5 px-4 text-sm sm:text-base uppercase tracking-[0.15em] sm:tracking-[0.3em] flex items-center justify-center gap-2 sm:gap-4 transition-colors overflow-hidden"
                  data-testid="experiences-submit-btn"
                >
                  {/* Left/right red accent rules to give it weight (desktop only) */}
                  <span aria-hidden className="hidden sm:block absolute inset-y-0 left-0 w-1 bg-red-800" />
                  <span aria-hidden className="hidden sm:block absolute inset-y-0 right-0 w-1 bg-red-800" />
                  {uploading ? (
                    <><Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin shrink-0" /> <span>Uploading evidence</span></>
                  ) : submitting ? (
                    <><Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin shrink-0" /> <span>Filing report</span></>
                  ) : (
                    <>
                      <span>File this report</span>
                      <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform shrink-0" strokeWidth={3} />
                    </>
                  )}
                </button>
                <p className="text-zinc-600 text-[10px] uppercase tracking-widest text-center mt-3 font-mono">
                  Every field stays anonymous · Reviewed by editor
                </p>
              </div>
            </form>
          </div>
        </section>
      )}

      {/* ============================================================== */}
      {/*                        THE REPORTS                              */}
      {/* ============================================================== */}
      <section className="border-b border-zinc-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="flex items-baseline justify-between mb-8 border-b border-zinc-900 pb-4">
            <div>
              <p className="text-red-500 text-[10px] font-black uppercase tracking-[0.35em] mb-1">
                On the record
              </p>
              <h2 className="text-white font-black text-3xl sm:text-4xl tracking-tight flex items-center gap-3">
                <ShieldCheck className="h-6 w-6 text-red-500" />
                The Reports
              </h2>
            </div>
            <span className="text-zinc-600 font-mono text-sm tabular-nums">
              {items.length} filed
            </span>
          </div>

          {loading && <p className="text-zinc-500 text-sm italic">Loading…</p>}

          {!loading && !windowClosed && items.length === 0 && (
            <div className="text-center py-16 border border-dashed border-zinc-800">
              <p className="text-zinc-500 text-sm italic mb-1">
                No reports approved yet.
              </p>
              <p className="text-zinc-600 text-xs">
                They&apos;ll appear here after our editor reviews each submission.
              </p>
            </div>
          )}

          {windowClosed && (
            <div className="text-center py-16 border border-dashed border-zinc-800">
              <p className="text-zinc-400 text-sm italic mb-1">
                This reporting window has closed.
              </p>
              <p className="text-zinc-600 text-xs">
                Any reports gathered in the last window are held privately by our editor.
              </p>
            </div>
          )}

          <div className="space-y-10">
            {items.map((it) => (
              <article
                key={it.public_number}
                className="border-l-2 border-red-600 pl-6"
                data-testid={`experience-item-${it.public_number}`}
              >
                <header className="mb-3">
                  <div className="flex items-center gap-3 mb-2">
                    <p className="text-red-500 font-black text-[11px] uppercase tracking-[0.35em]">
                      Experience №{String(it.public_number).padStart(2, '0')}
                    </p>
                    {it.is_confirmed && (
                      <span
                        className="inline-flex items-center gap-1 bg-emerald-950/60 text-emerald-300 border border-emerald-900/50 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest"
                        data-testid={`experience-confirmed-${it.public_number}`}
                      >
                        <Check className="h-3 w-3" /> Confirmed
                      </span>
                    )}
                  </div>
                  <p className="text-zinc-500 text-[11px] font-mono uppercase tracking-widest">
                    {formatDateline(it.when_iso)} · {it.location}
                  </p>
                </header>
                <p className="text-zinc-100 text-base leading-[1.7] font-serif whitespace-pre-wrap">
                  {it.description}
                </p>
                {it.media_urls && it.media_urls.length > 0 && (
                  <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {it.media_urls.map((url, i) => {
                      const isVideo = /\.(mp4|mov|webm)(\?|$)/i.test(url);
                      return isVideo ? (
                        <video key={i} src={url} controls className="rounded bg-black w-full h-36 object-cover" />
                      ) : (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                          <img src={url} alt="" loading="lazy" className="rounded bg-black w-full h-36 object-cover" />
                        </a>
                      );
                    })}
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/*                      IN IMMEDIATE DANGER                        */}
      {/* ============================================================== */}
      <section className="bg-[#0d0d0f]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <p className="text-zinc-500 text-[10px] font-black uppercase tracking-[0.35em] mb-4 text-center">
            If you are in immediate danger
          </p>
          <div className="grid sm:grid-cols-3 gap-3 text-center">
            <a href="tel:000" className="bg-red-950/30 hover:bg-red-950/50 border border-red-900/50 py-4 transition-colors">
              <p className="text-red-300 font-black text-lg">000</p>
              <p className="text-zinc-500 text-[10px] uppercase tracking-widest mt-1">Emergency</p>
            </a>
            <a href="tel:1800737732" className="bg-zinc-950 hover:bg-zinc-900 border border-zinc-800 py-4 transition-colors">
              <p className="text-white font-black text-lg">1800 RESPECT</p>
              <p className="text-zinc-500 text-[10px] uppercase tracking-widest mt-1">24/7 support</p>
            </a>
            <a href="tel:131114" className="bg-zinc-950 hover:bg-zinc-900 border border-zinc-800 py-4 transition-colors">
              <p className="text-white font-black text-lg">13 11 14</p>
              <p className="text-zinc-500 text-[10px] uppercase tracking-widest mt-1">Lifeline</p>
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
